import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { DatePicker } from "@/components/ui/date-picker";
import { useAuth } from "@/contexts/AuthContext";
import { useKantataProjects, useTherapyAreas, useAdminTemplateTypes } from "@/hooks/useMasterData";
import { masterDataApi } from "@/services/masterDataApi";
import { AI_MODELS } from "@/constants/aiModels";
import { useUsers } from "@/hooks/useUsers";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";

export interface ProjectFormValues {
  name: string;
  kantataProjectId: string;
  code: string; // display string derived from the selected Kantata project — still sent to API
  kantataProjectName?: string; // display label only — not sent to API
  clientIds: string[]; // derived read-only, from the selected Kantata project's associated clients
  clientNames?: string[]; // display labels only — not sent to API
  projectType: string;
  therapyArea: string;
  condition: string;
  objective: string;
  model: string;
  dataRetentionDefault?: string | null;
  memberIds: string[];
}

interface ProjectFormProps {
  defaultValues?: Partial<ProjectFormValues>;
  onSubmit: (values: ProjectFormValues) => void;
  isSubmitting: boolean;
  submitLabel: string;
  onCancel?: () => void;
  ownerId?: string;
  isEditing?: boolean;
}

const ProjectForm = ({
  defaultValues = {},
  onSubmit,
  isSubmitting,
  submitLabel,
  onCancel,
  ownerId,
  isEditing = false,
}: ProjectFormProps) => {
  const { user: currentUser } = useAuth();
  const { data: users = [] } = useUsers();
  const { data: therapyAreas = [] } = useTherapyAreas();
  const { data: allTemplateTypes = [] } = useAdminTemplateTypes();
  const activeTemplateTypes = allTemplateTypes.filter((t) => t.is_active);

  const [name, setName] = useState(defaultValues.name ?? "");
  const [code, setCode] = useState(defaultValues.code ?? "");
  const [kantataProjectId, setKantataProjectId] = useState(defaultValues.kantataProjectId ?? "");
  // Display code for the selected Kantata project — seeded from edit data, updated on selection
  const [selectedKantataCode, setSelectedKantataCode] = useState(defaultValues.kantataProjectName ?? "");
  const [kantataPopoverOpen, setKantataPopoverOpen] = useState(false);
  // Input value shown in the search box (raw, unthrottled)
  const [kantataInputValue, setKantataInputValue] = useState("");
  // Debounced search term — only updated 350 ms after the user stops typing
  const [kantataSearch, setKantataSearch] = useState("");
  const kantataDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { data: kantataResults = [], isFetching: kantataFetching } = useKantataProjects(kantataSearch);
  const queryClient = useQueryClient();
  // Derived read-only client list — populated from the selected Kantata project's
  // associated clients, seeded as-is from edit data (no fetch on load)
  const [clientIds, setClientIds] = useState<string[]>(defaultValues.clientIds ?? []);
  const [clientNames, setClientNames] = useState<string[]>(defaultValues.clientNames ?? []);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [projectType, setProjectType] = useState<string>(defaultValues.projectType ?? "");
  const [therapyArea, setTherapyArea] = useState(defaultValues.therapyArea ?? "");
  const [condition, setCondition] = useState(defaultValues.condition ?? "");
  const [objective, setObjective] = useState(defaultValues.objective ?? "");
  const [model, setModel] = useState(defaultValues.model ?? "opus-46");
  const [dataRetentionDefault, setDataRetentionDefault] = useState<string | null>(defaultValues.dataRetentionDefault ?? null);
  const [dataRetentionInvalid, setDataRetentionInvalid] = useState(false);
  const dataRetentionRef = useRef<HTMLDivElement>(null);
  const [memberIds, setMemberIds] = useState<string[]>(defaultValues.memberIds ?? []);
  const [memberPopoverOpen, setMemberPopoverOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const fieldRefs: Record<string, React.RefObject<HTMLDivElement | null>> = {
    name: useRef<HTMLDivElement>(null),
    kantataProjectId: useRef<HTMLDivElement>(null),
    projectType: useRef<HTMLDivElement>(null),
    therapyArea: useRef<HTMLDivElement>(null),
    condition: useRef<HTMLDivElement>(null),
    model: useRef<HTMLDivElement>(null),
  };

  const currentApiUser = useMemo(
    () => users.find((u) => u.email === currentUser?.email),
    [users, currentUser]
  );

  const effectiveOwnerId = ownerId ?? currentApiUser?.id;

  // Auto-add current user as owner on project creation (not in edit mode)
  useEffect(() => {
    if (ownerId) return;
    if (currentApiUser && !memberIds.includes(currentApiUser.id)) {
      setMemberIds((prev) => [currentApiUser.id, ...prev]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentApiUser?.id]);

  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      const aAdmin = a.role.toLowerCase() === "admin";
      const bAdmin = b.role.toLowerCase() === "admin";
      if (aAdmin !== bAdmin) return aAdmin ? -1 : 1;
      return a.email.localeCompare(b.email);
    });
  }, [users]);

  // Therapy area dropdown options: active items plus the saved value if it has been deactivated
  const therapyAreaOptions = useMemo(() => {
    if (
      therapyArea &&
      therapyAreas.length > 0 &&
      !therapyAreas.some((t) => t.name === therapyArea)
    ) {
      // Saved value is not in the active list — surface it so the user can see & re-select it
      return [{ id: `legacy-${therapyArea}`, name: therapyArea, is_active: false }, ...therapyAreas];
    }
    return therapyAreas;
  }, [therapyAreas, therapyArea]);

  const clearFieldError = useCallback((field: string) => {
    setValidationErrors((prev) => {
      if (!prev[field]) return prev;
      const { [field]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  const handleSubmit = () => {
    const errors: Record<string, string> = {};
    if (!name?.trim()) errors.name = 'Project Name is required.';
    if (!kantataProjectId) errors.kantataProjectId = 'Project Code (Kantata) is required.';
    if (!projectType) errors.projectType = 'Project Type is required.';
    if (!therapyArea) errors.therapyArea = 'Therapy Area (MeSH) is required.';
    if (!condition?.trim()) errors.condition = 'Condition is required.';
    if (!model) errors.model = 'Extraction Model is required.';
    if (dataRetentionInvalid) {
      dataRetentionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
      }
      return;
    }
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      const firstKey = Object.keys(errors)[0];
      fieldRefs[firstKey]?.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setValidationErrors({});
    onSubmit({ name: name.trim(), kantataProjectId, code, kantataProjectName: selectedKantataCode, clientIds, clientNames, projectType, therapyArea, condition: condition.trim(), objective, model, dataRetentionDefault, memberIds });
  };

  return (
    <>
      {/* Project Identification */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold text-brand-navy mb-4">Project Identification</h2>
        <div className="bg-card border rounded-lg p-6 space-y-5">
          <div ref={fieldRefs.name}>
            <Label className="font-semibold">Project Name <span className="text-destructive">*</span></Label>
            <Input placeholder="e.g., Oncology Value Dossier" value={name} onChange={(e) => { setName(e.target.value); clearFieldError('name'); }} className="mt-1.5" />
            {validationErrors.name && <p className="text-sm text-destructive mt-1">{validationErrors.name}</p>}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div ref={fieldRefs.kantataProjectId}>
              <Label className="font-semibold">Project Code (Kantata) <span className="text-destructive">*</span></Label>
              <Popover
                open={kantataPopoverOpen}
                onOpenChange={(open) => {
                  setKantataPopoverOpen(open);
                  if (!open) {
                    // Clear search state when popover closes
                    setKantataInputValue("");
                    setKantataSearch("");
                    if (kantataDebounceRef.current) clearTimeout(kantataDebounceRef.current);
                  }
                }}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={kantataPopoverOpen}
                    className="mt-1.5 w-full justify-between font-normal text-muted-foreground hover:text-muted-foreground hover:bg-inherit"
                  >
                    <span className="truncate">
                      {kantataProjectId && selectedKantataCode ? selectedKantataCode : "Select Kantata project..."}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start" style={{ width: "var(--radix-popover-trigger-width)" }}>
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder="Search by code or name..."
                      value={kantataInputValue}
                      onValueChange={(val) => {
                        setKantataInputValue(val);
                        // Debounce: fire API search 350 ms after user stops typing
                        if (kantataDebounceRef.current) clearTimeout(kantataDebounceRef.current);
                        kantataDebounceRef.current = setTimeout(() => setKantataSearch(val), 350);
                      }}
                    />
                    <CommandList>
                      {kantataFetching ? (
                        <CommandEmpty>Loading...</CommandEmpty>
                      ) : kantataResults.length === 0 ? (
                        <CommandEmpty>No Kantata projects found.</CommandEmpty>
                      ) : (
                        <CommandGroup>
                          {kantataResults.map((project) => (
                            <CommandItem
                              key={project.id}
                              value={project.id}
                              onSelect={() => {
                                if (project.id === kantataProjectId) {
                                  setKantataProjectId("");
                                  setSelectedKantataCode("");
                                  setCode("");
                                  setClientIds([]);
                                  setClientNames([]);
                                } else {
                                  setKantataProjectId(project.id);
                                  setSelectedKantataCode(project.code ?? "");
                                  setCode(project.code ?? "");
                                  clearFieldError('kantataProjectId');
                                  setClientsLoading(true);
                                  queryClient
                                    .fetchQuery({
                                      queryKey: ["master", "kantata-projects", "by-id", project.id, "clients"],
                                      queryFn: () => masterDataApi.kantataProjectClients(project.id),
                                      staleTime: 300_000,
                                    })
                                    .then((clients) => {
                                      setClientIds(clients.map((c) => c.id));
                                      setClientNames(clients.map((c) => c.name));
                                    })
                                    .catch(() => {
                                      setClientIds([]);
                                      setClientNames([]);
                                    })
                                    .finally(() => setClientsLoading(false));
                                }
                                setKantataPopoverOpen(false);
                              }}
                            >
                              <Check className={cn("mr-2 h-4 w-4 shrink-0", kantataProjectId === project.id ? "opacity-100" : "opacity-0")} />
                              <span className="truncate">{project.code}</span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {validationErrors.kantataProjectId && <p className="text-sm text-destructive mt-1">{validationErrors.kantataProjectId}</p>}
            </div>
            <div>
              <Label className="font-semibold">Client</Label>
              {/* Read-only, derived from the selected Kantata code — no manual add/remove/edit */}
              <div className="mt-1.5 min-h-9 w-full rounded-md border border-dashed bg-muted/50 px-3 py-1.5 cursor-not-allowed">
                {clientsLoading ? (
                  <span className="text-sm text-muted-foreground">Loading clients...</span>
                ) : clientNames.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {clientNames.map((name, i) => (
                      <Badge key={clientIds[i] ?? name} variant="secondary" className="pointer-events-none">
                        {name}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <span className="text-sm text-muted-foreground">
                    {kantataProjectId ? "No clients associated with this code" : "Select a Kantata code to populate clients"}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div ref={fieldRefs.projectType}>
              <Label className="font-semibold">Project Type <span className="text-destructive">*</span></Label>
              <Select key={activeTemplateTypes.length} value={projectType} onValueChange={(v) => { setProjectType(v); clearFieldError('projectType'); }} disabled={isEditing}>
                <SelectTrigger className="mt-1.5" data-testid="project-type-trigger"><SelectValue placeholder="Select project type..." /></SelectTrigger>
                <SelectContent>
                  {activeTemplateTypes.map((t) => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {validationErrors.projectType && <p className="text-sm text-destructive mt-1">{validationErrors.projectType}</p>}
            </div>
            <div>
              <Label className="font-semibold">Data Retention</Label>
              <DatePicker
                ref={dataRetentionRef}
                value={dataRetentionDefault}
                onChange={setDataRetentionDefault}
                onValidityChange={setDataRetentionInvalid}
                placeholder="Select default retention date..."
                className="mt-1.5"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Clinical Information */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold text-brand-navy mb-4">Clinical Information</h2>
        <div className="bg-card border rounded-lg p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div ref={fieldRefs.therapyArea}>
              <Label className="font-semibold">Therapy Area (MeSH) <span className="text-destructive">*</span></Label>
              <Select value={therapyArea} onValueChange={(v) => { setTherapyArea(v); clearFieldError('therapyArea'); }}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select therapy area..." /></SelectTrigger>
                <SelectContent>
                  {therapyAreaOptions.map((t) => (
                    <SelectItem key={t.id} value={t.name}>
                      {t.name}{!t.is_active && " (inactive)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {validationErrors.therapyArea && <p className="text-sm text-destructive mt-1">{validationErrors.therapyArea}</p>}
            </div>
            <div ref={fieldRefs.condition}>
              <Label className="font-semibold">Condition <span className="text-destructive">*</span></Label>
              <Input
                maxLength={100}
                placeholder="Enter condition..."
                value={condition}
                onChange={(e) => { setCondition(e.target.value); clearFieldError('condition'); }}
                className="mt-1.5"
              />
              {validationErrors.condition && <p className="text-sm text-destructive mt-1">{validationErrors.condition}</p>}
            </div>
          </div>
          <div>
            <Label className="font-semibold">Project Objectives</Label>
            <Textarea placeholder="Describe the key objectives and deliverables for this project..." className="mt-1.5 min-h-[100px]" value={objective} onChange={(e) => setObjective(e.target.value)} />
          </div>
        </div>
      </section>

      {/* AI Model */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold text-brand-navy mb-4">AI Model</h2>
        <div className="bg-card border rounded-lg p-6">
          <div ref={fieldRefs.model}>
          <Label className="font-semibold">
            Extraction Model <span className="text-destructive">*</span>
          </Label>
          <Select value={model} onValueChange={(v) => { setModel(v); clearFieldError('model'); }}>
            <SelectTrigger className="mt-1.5 w-1/2">
              <SelectValue placeholder="Select AI model..." />
            </SelectTrigger>
            <SelectContent>
              {AI_MODELS.map((m) => (
                <SelectItem key={m.name} value={m.value}>{m.displayName}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-sm text-muted-foreground mt-2">
            This model will be used for this project's primary Model.
          </p>
          {validationErrors.model && <p className="text-sm text-destructive mt-1">{validationErrors.model}</p>}
          </div>
        </div>
      </section>

      {/* Project Team */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold text-brand-navy mb-4">Project Team</h2>
        <div className="bg-card border rounded-lg p-6">
          <Label className="font-semibold">Project Team Members (Active Directory)</Label>
          <Popover open={memberPopoverOpen} onOpenChange={setMemberPopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={memberPopoverOpen}
                className="mt-1.5 w-full justify-between font-normal text-muted-foreground hover:text-muted-foreground hover:bg-inherit"
              >
                {memberIds.length > 0
                  ? `${memberIds.length} member${memberIds.length > 1 ? "s" : ""} selected`
                  : "Select team members from Active Directory..."}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-full p-0" align="start" style={{ width: "var(--radix-popover-trigger-width)" }}>
              <Command>
                <CommandInput placeholder="Search members..." />
                <CommandList>
                  <CommandEmpty>No members found.</CommandEmpty>
                  <CommandGroup>
                    {sortedUsers.map((user) => {
                      const isSelected = memberIds.includes(user.id);
                      const isOwner = user.id === effectiveOwnerId;
                      return (
                        <CommandItem
                          key={user.id}
                          value={user.email}
                          onSelect={() => {
                            if (isOwner) return;
                            setMemberIds((prev) =>
                              isSelected ? prev.filter((v) => v !== user.id) : [...prev, user.id]
                            );
                          }}
                          className={cn(isOwner && "pointer-events-none opacity-60")}
                        >
                          <Check className={cn("mr-2 h-4 w-4", isSelected ? "opacity-100" : "opacity-0")} />
                          {user.email}
                          {isOwner && <span className="ml-auto text-xs text-muted-foreground">owner</span>}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          {memberIds.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {memberIds.map((memberId) => {
                const user = users.find((u) => u.id === memberId);
                if (!user) return null;
                return (
                  <Badge key={memberId} variant="secondary" className="gap-1 pr-1">
                    {user.email}
                    {memberId !== effectiveOwnerId ? (
                      <button
                        type="button"
                        aria-label={`Remove ${user.email}`}
                        onClick={() => setMemberIds((prev) => prev.filter((v) => v !== memberId))}
                        className="rounded-full hover:bg-muted p-0.5"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    ) : (
                      <span className="px-0.5 text-xs text-muted-foreground">owner</span>
                    )}
                  </Badge>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <div className="flex justify-end gap-3">
        {onCancel && (
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button
          className="bg-brand-teal text-white hover:bg-brand-teal/90 px-8"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Saving..." : submitLabel}
        </Button>
      </div>
    </>
  );
};

export default ProjectForm;
